// Need to import the appropriate rendering profiles because of a breaking change in v18.0.0 (https://github.com/Kitware/vtk-js/releases/tag/v18.0.0)
import "vtk.js/Sources/Rendering/Profiles/Volume";

import vtkGenericRenderWindow from "vtk.js/Sources/Rendering/Misc/GenericRenderWindow";
//import vtkOpenGLRenderWindow from 'vtk.js/Sources/Rendering/OpenGL/RenderWindow';
// Force the loading of HttpDataAccessHelper to support gzip decompression
import "vtk.js/Sources/IO/Core/DataAccessHelper/HttpDataAccessHelper";

import vtkWidgetManager from "vtk.js/Sources/Widgets/Core/WidgetManager";
import vtkPaintWidget from "../Paint/PaintWidget";
import vtkPaintFilter from "./PaintFilter";
import SegmentationPaintingMode from "./PaintFilter";
import { ViewTypes } from "vtk.js/Sources/Widgets/Core/WidgetManager/Constants";

import vtkImageMapper from "vtk.js/Sources/Rendering/Core/ImageMapper";
import vtkImageReslice from "vtk.js/Sources/Imaging/Core/ImageReslice";
import vtkPlane from "vtk.js/Sources/Common/DataModel/Plane";
import vtkImageData from "vtk.js/Sources/Common/DataModel/ImageData";
import ImageConstants from "vtk.js/Sources/Rendering/Core/ImageMapper/Constants";

import vtkPiecewiseFunction from "vtk.js/Sources/Common/DataModel/PiecewiseFunction";
import vtkColorTransferFunction from "vtk.js/Sources/Rendering/Core/ColorTransferFunction";

//// DEBUG
//import vtkConeSource from 'vtk.js/Sources/Filters/Sources/ConeSource';
//import vtkMapper from 'vtk.js/Sources/Rendering/Core/Mapper';
//import vtkActor from 'vtk.js/Sources/Rendering/Core/Actor';
import React, { useEffect, useRef, useState } from "react";
import { Vector3 } from "./Vector3";
import { vec3 } from "gl-matrix";

import {
    logDebug,
    ignore,
    debugCallback,
    isNullOrUndefined,
    getDUName,
    WidgetType,
    SlicingModeName,
    getTopLevelDUName,
} from "./Common";
import customImageInteractor from "./CustomImageInteractor";
import vtkImageSlice from "vtk.js/Sources/Rendering/Core/ImageSlice";
import vtkMath from "vtk.js/Sources/Common/Core/Math";
import vtkMatrixBuilder from "vtk.js/Sources/Common/Core/MatrixBuilder";
import vtkCoordinate from "vtk.js/Sources/Rendering/Core/Coordinate";

import { useScreenshot } from "../Screenshot/Screenshot";
import { useInteraction } from "./Interaction";
import { InterpolationType } from "vtk.js/Sources/Rendering/Core/ImageProperty/Constants";
import { useDistanceWidget } from "../Distance/Distance";
import { useAngleWidget } from "../Angle/Angle";
import useCrosshairsWidget from "../Crosshairs/Crosshairs";
import useDiameterLines from "./DiameterLines";

import { Union } from "fable-compiler/bin/fable-library/Types";

import Konva from "konva";

const AXIAL_EMPTY_SEGMENTATION_LABEL = -1024;
const CENTERLINE_EMPTY_SEGMENTATION_LABEL = 0;

// Corresponds to the SegmentationColor DU in F#.
const SegmentationColor = Object.freeze({
    Red: "Red",
    Blue: "Blue",
});

const redTransferFunction = vtkColorTransferFunction.newInstance();
redTransferFunction.addRGBPoint(1, 1, 0, 0);
const blueTransferFunction = vtkColorTransferFunction.newInstance();
blueTransferFunction.addRGBPoint(1, 0, 0, 1);

function getTransferFunctionByColor(color) {
    switch (getTopLevelDUName(color)) {
        case SegmentationColor.Red:
            return redTransferFunction;
        case SegmentationColor.Blue:
            return blueTransferFunction;
        default:
            return redTransferFunction;
    }
}

const isDebugging = false;

function log(msg, ...params) {
    logDebug("DicomVisualization", isDebugging, msg, ...params);
}

// Global storage for manual zoom state, persists across component remounts.
// Keyed by viewport ID.
const manualZoomStateByViewport = new Map();

function debug(callback) {
    debugCallback(isDebugging, callback);
}

function brushSizeToRadius(brushSize) {
    return brushSize / 2.0 - 0.1;
}

/**
 * Helper function to calculate the corrected Z coordinate for painting on boundary slices.
 * When the slice location is at or outside the image bounds, we nudge it inward so that
 * the world-to-index transform rounds to the last valid voxel index instead of one past it.
 *
 * Example: with origin -384.75, spacing 1.25, and 241 slices the max bound is -84.125.
 * A slice location of exactly -84.125 maps to raw index 240.5, which Math.round pushes
 * to 241 — one past the last valid index (240).  Subtracting epsilon keeps it at 240.
 *
 * @param {number} sliceLocation - The current slice location
 * @param {number} minBound - The minimum bound for the slicing axis
 * @param {number} maxBound - The maximum bound for the slicing axis
 * @returns {number} The corrected Z coordinate for painting
 */
function getCorrectedPaintZ(sliceLocation, minBound, maxBound) {
    const epsilon = 0.001;
    if (sliceLocation >= maxBound) {
        return maxBound - epsilon;
    } else if (sliceLocation <= minBound) {
        return minBound + epsilon;
    }
    return sliceLocation;
}

/**
 * Helper function to calculate the manipulator Z position for brush outline visibility.
 * Returns null if no adjustment is needed.
 *
 * The VTK paint widget renders its brush outline as an SVG via the widget manager.
 * When the manipulator origin sits exactly on (or beyond) the volume boundary,
 * the renderer's clipping planes can clip the outline away, making the brush
 * invisible even though painting still works.  We nudge the manipulator origin
 * inward by half a voxel spacing so it always falls inside the clipping volume.
 *
 * @param {number} sliceLocation - The current slice location
 * @param {number} minBound - The minimum bound for the slicing axis
 * @param {number} maxBound - The maximum bound for the slicing axis
 * @param {number} spacing - The image spacing along the slicing axis
 * @returns {number | null} The adjusted Z coordinate, or null if no adjustment needed
 */
function getManipulatorAdjustment(sliceLocation, minBound, maxBound, spacing) {
    // Half a voxel spacing is enough to move inside the clipping volume while
    // keeping the visual position indistinguishable from the actual slice.
    const halfSpacing = spacing * 0.5;

    // Treat anything within half a spacing of the boundary as "on the boundary".
    const distanceFromMax = maxBound - sliceLocation;
    const distanceFromMin = sliceLocation - minBound;

    if (distanceFromMax < halfSpacing) {
        // At or beyond the max boundary — pull inward.
        return maxBound - halfSpacing;
    }

    if (distanceFromMin < halfSpacing) {
        // At or beyond the min boundary — push inward.
        return minBound + halfSpacing;
    }

    return null;
}

/**
 * F# SlicingMode union type.
 * @typedef {Union} SlicingMode
 */

/**
 * F# SegmentationSliceData type
 * @typedef {Object} SegmentationSliceData
 * @property {Int16Array} buffer - The segmentation data buffer.
 * @property {number[]} dimensions - The dimensions of the segmentation slice.
 * @property {number[]} origin - The origin coordinates of the segmentation slice.
 * @property {number[]} spacing - The spacing values of the segmentation slice.
 */

/**
 * @param {Object} props
 * @param {string} props.viewportId
 * @param {File[]} props.imageFiles
 * @param {File[]} props.maskFiles
 * @param {boolean} props.showMasks
 * @param {function} props.onSliceChange
 * @param {function} props.onCenterlineSliceScroll
 * @param {function} props.onTouchStart
 * @param {function} props.onTouchEnd
 * @param {Object} props.interactionModeProps
 * @param {Object} props.screenshotProps
 * @param {boolean} props.isSliceScroll
 * @param {number} [props.slicePlaneIndex]
 * @param {boolean} props.isCenterline
 * @param {Object} props.segmentationProps
 * @param {string} props.segmentationColor
 * @param {function} props.centerlineSegmentationChangedCallback
 * @param {function} props.axialSegmentationChangedCallback
 * @param {Object} props.lengthProps
 * @param {Object} props.angleProps
 * @param {boolean} [props.isVisible]
 * @param {SlicingMode} props.slicingMode
 * @param {Union} [props.activeWidget]
 * @param {Union} [props.activeMeasurement]
 * @param {CrosshairsWidgetFactory} [props.crosshairsWidgetFactory]
 * @param {Object} props.eventNames
 * @param {Object} props.imageLoader
 * @param {function} props.onBoundsAcquired
 * @param {Array.<[number, SegmentationSliceData]>} props.modifiedSegmentations
 * @param {function} props.onVisualizationReady - Callback to invoke when the visualization is ready.
 */
const DicomVisualization = ({
    viewportId,
    imageFiles: imageFiles,
    maskFiles: maskFiles,
    showMasks,
    onSliceChange,
    onCenterlineSliceScroll,
    onTouchStart,
    onTouchEnd,
    interactionModeProps,
    screenshotProps,
    isSliceScroll,
    slicePlaneIndex,
    isCenterline,
    segmentationProps,
    segmentationColor,
    centerlineSegmentationChangedCallback,
    axialSegmentationChangedCallback,
    lengthProps,
    angleProps,
    isVisible = true,
    slicingMode,
    activeWidget = null,
    activeMeasurement = null,
    crosshairsWidgetFactory,
    eventNames,
    imageLoader: imageLoader,
    onBoundsAcquired,
    modifiedSegmentations,
    onVisualizationReady
}) => {
    const containerRef = useRef(null);
    const context = useRef(null);
    const centerlineSegmentationChangedCallbackRef = useRef(null);
    const axialSegmentationChangedCallbackRef = useRef(null);
    const maskFilesRef = useRef(maskFiles);
    const modifiedSegmentationsRef = useRef(modifiedSegmentations);
    const ignoreSliceChangeRef = useRef(false);
    const [diameterLinesTrigger, setDiameterLinesTrigger] = useState(false);

    // Initialize manual zoom state from global Map (persists across remounts).
    const hasManualZoomRef = useRef(
        manualZoomStateByViewport.get(viewportId) || false,
    );
    const hasBeenVisibleRef = useRef(isVisible);

    const { SlicingMode } = ImageConstants;

    function _captureCustomScreenshot() {
        return new Promise((resolve, reject) => {
            context.current.renderWindow.render();
            const viewportCanvas = context.current.genericRenderWindow
                .getApiSpecificRenderWindow()
                .getCanvas();
            if (!viewportCanvas) {
                reject(`Unable to find canvas for viewport ${viewportId}`);
                return;
            }

            const dpr = window.devicePixelRatio;

            const offscreenCanvas = document.createElement("canvas");
            // SVGs do not respect the scaling passed into CanvasContext.drawImage, so instead of doing a bunch of
            // futzing to force the SVG to scale up with the DPR, we will just scale the canvas down.
            // TODO: this approach results in a lower quality image, we should fix it later when we have more time
            offscreenCanvas.width = viewportCanvas.width / dpr;
            offscreenCanvas.height = viewportCanvas.height / dpr;

            if (viewportCanvas.width === 0 || viewportCanvas.height === 0) {
                reject(
                    `Trying to take a screenshot of a viewport ${viewportId}, not being shown`,
                );
                return;
            }

            const offscreenCtx = offscreenCanvas.getContext("2d");
            offscreenCtx.drawImage(
                viewportCanvas,
                0,
                0,
                offscreenCanvas.width,
                offscreenCanvas.height,
            );

            const konvaStage = context.current.konvaStage;
            if (konvaStage) {
                const konvaCanvas = konvaStage.toCanvas();
                offscreenCtx.drawImage(
                    konvaCanvas,
                    0,
                    0,
                    offscreenCanvas.width,
                    offscreenCanvas.height,
                );
            }

            const finalizeScreenshot = () => {
                const canvasInfo = {
                    viewportIndex: viewportId,
                    canvasData: offscreenCanvas.toDataURL(),
                };
                resolve(canvasInfo);
            };

            const svgElements = Array.from(
                viewportCanvas.parentElement?.querySelectorAll("svg") || [],
            );
            if (svgElements.length === 0) {
                console.warn(
                    `Unable to find annotations for viewport ${viewportId}, rendering without annotations`,
                );
                finalizeScreenshot();
                return;
            }

            const drawSvgAtIndex = (index) => {
                if (index >= svgElements.length) {
                    finalizeScreenshot();
                    return;
                }

                const svg = svgElements[index];
                const outerHTML = svg.outerHTML;
                // VTK is evil and doesn't actually make the tag a valid SVG file.
                const hasNamespace = outerHTML.includes("xmlns=");
                const validSvg = hasNamespace
                    ? outerHTML
                    : outerHTML.replace(
                        "<svg",
                        '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"',
                    );
                const blob = new Blob([validSvg], { type: "image/svg+xml" });
                const url = URL.createObjectURL(blob);

                const svgImg = new Image();

                svgImg.onload = function () {
                    offscreenCtx.drawImage(
                        svgImg,
                        0,
                        0,
                        offscreenCanvas.width,
                        offscreenCanvas.height,
                    );
                    URL.revokeObjectURL(url);
                    drawSvgAtIndex(index + 1);
                };

                const rejectHandler = function (ev) {
                    URL.revokeObjectURL(url);
                    // Would it be better to fail here?
                    console.warn(
                        `Unable to render annotations for viewport ${viewportId}, rendering without annotations`,
                        ev,
                    );
                    drawSvgAtIndex(index + 1);
                };
                svgImg.oncancel = rejectHandler;
                svgImg.onabort = rejectHandler;
                svgImg.onerror = rejectHandler;

                svgImg.width = offscreenCanvas.width;
                svgImg.height = offscreenCanvas.height;
                svgImg.src = url;
            };

            drawSvgAtIndex(0);
        });
    }

    function captureCustomScreenshot(ev) {
        if (ev.detail.viewportId !== viewportId) {
            return;
        }

        if (typeof ev.detail.callback !== "function") {
            console.error("Invalid event detail for screenshot capture");
            return;
        }

        // Wait for a short delay to ensure all rendering is complete.
        setTimeout(async () => {
            try {
                const ret = await _captureCustomScreenshot();
                ev.detail.callback(ret);
            } catch (e) {
                console.error(e);
            }
        }, 100);
    }

    const render = () => {
        if (context.current?.genericRenderWindow) {
            log("Rendering");
            // OPTIMIZE:
            // This is a quick fix for the resolution issue when switching between single/quad view.
            // A better fix would be to depend on a visibility prop.
            context.current.genericRenderWindow.resize();
        }
    };

    const calculateCenterlineParallelScale = () => {
        if (
            !isCenterline ||
            !context.current?.renderer ||
            !context.current?.mapper
        ) {
            return;
        }

        const containerWidth = containerRef.current?.clientWidth;
        const containerHeight = containerRef.current?.clientHeight;

        // Don't calculate if container is not visible yet.
        if (!containerWidth || !containerHeight) {
            return;
        }

        const imageData = context.current.mapper.getInputData();
        if (!imageData) {
            return;
        }

        const bounds = imageData.getBounds();
        const containerAspect = containerWidth / containerHeight;

        // Get image dimensions based on slicing mode.
        let imageWidth, imageHeight;
        switch (getDUName(slicingMode)) {
            case SlicingModeName.SAGITTAL:
                imageWidth = bounds[3] - bounds[2]; // Y extent
                imageHeight = bounds[5] - bounds[4]; // Z extent
                break;
            case SlicingModeName.CORONAL:
                imageWidth = bounds[1] - bounds[0]; // X extent
                imageHeight = bounds[5] - bounds[4]; // Z extent
                break;
            case SlicingModeName.AXIAL:
            default:
                imageWidth = bounds[1] - bounds[0]; // X extent
                imageHeight = bounds[3] - bounds[2]; // Y extent
                break;
        }

        const imageAspect = imageWidth / imageHeight;
        const camera = context.current.renderer.getActiveCamera();

        // Set parallel scale to fit image in container.
        // Parallel scale is half the height of the view frustum.
        if (containerAspect > imageAspect) {
            // Container is wider than image, fit to height.
            camera.setParallelScale(imageHeight / 2);
        } else {
            // Container is taller than image, fit to width.
            camera.setParallelScale(imageWidth / (2 * containerAspect));
        }

        context.current.renderer.resetCameraClippingRange();
    };

    const handleResize = () => {
        const container = containerRef.current;
        const ctx = context.current;

        if (!container || !ctx?.genericRenderWindow) {
            return;
        }

        ctx.genericRenderWindow.resize();

        const { width, height } = container.getBoundingClientRect();

        for (const callback of ctx.resizeCallbacks) {
            callback(width, height);
        }

        const stage = ctx.konvaStage;
        if (stage) {
            stage.size({ width: width, height: height });
            stage.batchDraw();
        }

        // Only auto-calculate parallel scale if user hasn't manually zoomed.
        if (!hasManualZoomRef.current) {
            calculateCenterlineParallelScale();
        }

        ctx.renderWindow.render();
    };

    render();

    /**
     * @param {boolean} shouldBeCenterline
     * @param {Union[][]} modifiedMaskSlices
     * @private
     */
    const _updateMaskSlices = (maskFiles, modifiedMaskSlices) => {
        if (
            context.current !== null &&
            context.current.painter !== null &&
            modifiedMaskSlices !== null &&
            maskFiles !== null
        ) {
            const baselineScalars = imageLoader.tryGetMaskBaseline(
                maskFiles
            );

            // This should only happen if the segmentation isn't loaded yet
            if (isNullOrUndefined(baselineScalars)) return;

            // Copy baseline
            const mask = context.current.painter.getLabelMap();
            const newScalars = baselineScalars.newClone();
            newScalars.deepCopy(baselineScalars);
            const data = newScalars.getData();

            // Inject modified slices
            const [nx, ny, _] = mask.getDimensions();
            const sliceSize = nx * ny;

            for (let i = 0; i < modifiedMaskSlices.length; i++) {
                const sliceIndex = modifiedMaskSlices[i][0];
                const sliceData = modifiedMaskSlices[i][1];

                const offset = sliceIndex * sliceSize;

                data.set(sliceData.buffer, offset);
            }

            mask.getPointData().setScalars(newScalars);
            mask.modified();
            setDiameterLinesTrigger((prev) => !prev);
        }
    };

    // Effect that only runs when the component mounts and unmounts.
    useEffect(() => {
        log("Mounting");
        onVisualizationReady?.([viewportId, false]);
        const container = containerRef.current;

        if (container) {
            const resizeObserver = new ResizeObserver(handleResize);
            resizeObserver.observe(container);
            context.current = {
                ...context.current,
                resizeObserver,
            };
        }

        return () => {
            // Just using a more convenient name; no magic here.
            const ctx = context.current;

            if (ctx) {
                log("Unmounting");
                const container = containerRef.current;

                const {
                    genericRenderWindow,
                    onModifiedSub,
                    actor,
                    maskActor,
                    mapper,
                    maskMapper,
                    istyle,
                    interactor,
                    widgetManager,
                    combinedTouchStart,
                    combinedTouchEnd,
                    handleWheel,
                    resizeObserver,
                } = ctx;

                if (container) {
                    container.removeEventListener(
                        "touchstart",
                        combinedTouchStart,
                    );
                    container.removeEventListener("touchend", combinedTouchEnd);
                    container.removeEventListener(
                        "touchmove",
                        istyle.handleTouchMove,
                    );
                    container.removeEventListener("wheel", handleWheel);
                    // Remove our custom mouse move handler
                    const handleMouseMove = container._brushMouseMoveHandler;
                    if (handleMouseMove) {
                        container.removeEventListener(
                            "mousemove",
                            handleMouseMove,
                        );
                        delete container._brushMouseMoveHandler;
                    }
                }

                if (eventNames?.CaptureCustomScreenshot) {
                    window.removeEventListener(
                        eventNames.CaptureCustomScreenshot,
                        captureCustomScreenshot,
                    );
                }
                //Disable interactor to prevent any animations for doing anything
                interactor?.disable();
                genericRenderWindow?.delete();

                widgetManager?.removeWidgets();
                widgetManager?.releaseFocus();

                resizeObserver?.disconnect();
                onModifiedSub?.unsubscribe();
                actor?.delete();
                mapper?.delete();
                maskActor?.delete();
                maskMapper?.delete();
                widgetManager?.delete();
                context.current = null;
            }
        };
    }, []);

    useEffect(() => {
        centerlineSegmentationChangedCallbackRef.current =
            centerlineSegmentationChangedCallback;
    }, [centerlineSegmentationChangedCallback]);

    useEffect(() => {
        axialSegmentationChangedCallbackRef.current =
            axialSegmentationChangedCallback;
    }, [axialSegmentationChangedCallback]);

    // Effect to set up the DICOM visualization.
    useEffect(() => {
        const container = containerRef.current;
        if (
            !context.current?.actor &&
            imageFiles &&
            imageFiles.length > 0 &&
            container
        ) {
            log(`Reading ${imageFiles.length} dicom files`);

            context.current.viewportId = viewportId;
            context.current.resizeCallbacks = new Set();

            const konvaContainer = document.createElement("div");
            konvaContainer.id = `dicom-konva-container-${viewportId}`;
            konvaContainer.style.position = "absolute";
            konvaContainer.style.top = "0";
            konvaContainer.style.left = "0";
            konvaContainer.style.width = "100%";
            konvaContainer.style.height = "100%";
            konvaContainer.style.pointerEvents = "none";
            konvaContainer.style.zIndex = "1";
            container.appendChild(konvaContainer);

            const stage = new Konva.Stage({
                container: konvaContainer,
                width: container.offsetWidth,
                height: container.offsetHeight,
            });

            context.current.konvaStage = stage;

            // We use the wrapper here to abstract out manual RenderWindow/Renderer/OpenGLRenderWindow setup
            const genericRenderWindow = vtkGenericRenderWindow.newInstance({
                background: [0.0, 0.0, 0.0],
            });

            //var genericRenderWindow = vtkOpenGLRenderWindow.newInstance();
            genericRenderWindow.setContainer(containerRef.current);
            genericRenderWindow.resize();

            const renderer = genericRenderWindow.getRenderer();
            const renderWindow = genericRenderWindow.getRenderWindow();
            debug(() => (global.debug_renderWindow = renderWindow));

            // renderer camera to parallel projection
            renderer.getActiveCamera().setParallelProjection(true);

            // --- Set up interactor style for image slicing
            const istyle = customImageInteractor.newInstance();

            // Interaction styles don't provide the ability to distinguish between a touch and left mouse press,
            // so we have to add that functionality ourselves
            const combinedTouchStart = (e) => {
                onTouchStart(e);
                istyle.handleTouchStart(e);
            };
            const combinedTouchEnd = (e) => {
                onTouchEnd(e);
                istyle.handleTouchEnd(e);
            };

            containerRef.current.addEventListener(
                "touchstart",
                combinedTouchStart,
            );
            containerRef.current.addEventListener("touchend", combinedTouchEnd);
            containerRef.current.addEventListener(
                "touchmove",
                istyle.handleTouchMove,
            );

            istyle.setContainer(containerRef.current);
            istyle.setRenderWindow(renderWindow);
            istyle.setMouseWheelIsSliceScroll(isSliceScroll);

            if (!isNullOrUndefined(onCenterlineSliceScroll)) {
                istyle.setSliceScrollCallback(onCenterlineSliceScroll);
            } else {
                const onSliceScroll = (delta) => {
                    // Apply the distance change first (this moves the camera position,
                    // which indirectly shifts the focal point along the DOP).
                    istyle.recalculateDistance(renderer, delta * -2);

                    // After the distance change, clamp the focal point to the image bounds
                    // so the user cannot scroll past the first/last slice in the volume.
                    const currentImageData = mapper.getInputData();
                    if (currentImageData) {
                        const imgBounds = currentImageData.getBounds();
                        const slicingModeAxis = mapper.getSlicingMode() % 3;
                        const minBound = imgBounds[slicingModeAxis * 2];
                        const maxBound = imgBounds[slicingModeAxis * 2 + 1];

                        const camera = renderer.getActiveCamera();
                        const focalPoint = camera.getFocalPoint();
                        const currentSlice = focalPoint[slicingModeAxis];

                        if (
                            currentSlice < minBound ||
                            currentSlice > maxBound
                        ) {
                            const clampedSlice = Math.max(
                                minBound,
                                Math.min(maxBound, currentSlice),
                            );
                            const newFocalPoint = [...focalPoint];
                            newFocalPoint[slicingModeAxis] = clampedSlice;
                            camera.setFocalPoint(...newFocalPoint);

                            // Also move the camera position by the same amount so that
                            // the distance (and thus the internal state) stays consistent.
                            const offset = clampedSlice - currentSlice;
                            const position = camera.getPosition();
                            const newPosition = [...position];
                            newPosition[slicingModeAxis] += offset;
                            camera.setPosition(...newPosition);
                        }
                    }

                    ignoreSliceChangeRef.current = true;
                };
                istyle.setSliceScrollCallback(onSliceScroll);
            }

            istyle.setInteractionMode("IMAGE_SLICING");
            renderWindow.getInteractor().setInteractorStyle(istyle);

            const interactor = renderWindow.getInteractor();

            // Track manual zoom changes on centerline views.
            const handleWheel = () => {
                // Check if this is a centerline view and wheel is set to zoom (not slice scroll).
                if (
                    isCenterline &&
                    istyle &&
                    !istyle.getMouseWheelIsSliceScroll()
                ) {
                    // User is zooming manually, mark the flag and save to global Map.
                    hasManualZoomRef.current = true;
                    manualZoomStateByViewport.set(viewportId, true);
                }
            };

            container.addEventListener("wheel", handleWheel);

            // BRUSH FIX: Add mouse move handler to maintain brush outline on edge slices
            // VTK's paint widget loses outline visibility when the mouse moves on boundary slices
            const handleMouseMove = (e) => {
                if (
                    !paintHandle.getVisibility() ||
                    getDUName(activeWidget) !== WidgetType.SEGMENTATION_WIDGET
                ) {
                    return;
                }

                const imageData = mapper.getInputData();
                if (!imageData) return;

                const imageBounds = imageData.getBounds();
                const imageSpacing = imageData.getSpacing();
                const slicingMode = mapper.getSlicingMode() % 3;
                const minBound = imageBounds[slicingMode * 2];
                const maxBound = imageBounds[slicingMode * 2 + 1];
                const spacing = imageSpacing[slicingMode];
                const sliceLocation = mapper.getSlice();

                const adjustment = getManipulatorAdjustment(
                    sliceLocation,
                    minBound,
                    maxBound,
                    spacing,
                );

                if (adjustment !== null) {
                    const manipulatorOrigin = paintWidget
                        .getManipulator()
                        .getUserOrigin();
                    if (manipulatorOrigin) {
                        const currentZ = manipulatorOrigin[slicingMode];
                        if (Math.abs(currentZ - adjustment) > 0.01) {
                            const center = imageData.getCenter();
                            const adjustedOrigin = [
                                center[0],
                                center[1],
                                center[2],
                            ];
                            adjustedOrigin[slicingMode] = adjustment;
                            paintWidget
                                .getManipulator()
                                .setUserOrigin(adjustedOrigin);
                            paintHandle.updateRepresentationForRender();
                        }
                    }
                }
            };

            // Store reference for cleanup and add the event listener
            container._brushMouseMoveHandler = handleMouseMove;
            container.addEventListener("mousemove", handleMouseMove);

            // --- Set up the slicing actor ---

            const actor = vtkImageSlice.newInstance();
            const mapper = vtkImageMapper.newInstance();
            const slicePlane = vtkPlane.newInstance();
            debug(() => (global.debug_actor = actor));
            debug(() => (global.debug_mapper = mapper));
            debug(() => (global.debug_renderer = renderer));
            debug(() => (global.debug_slicePlane = slicePlane));
            debug(() => (global.debug_resliceFilter = resliceFilter));
            debug(() => (global.debug_transform = transform));

            mapper.setSliceAtFocalPoint(true);

            switch (slicingMode.name) {
                case SlicingModeName.SAGITTAL:
                    mapper.setSlicingMode(SlicingMode.X);
                    break;
                case SlicingModeName.CORONAL:
                    mapper.setSlicingMode(SlicingMode.Y);
                    break;
                case SlicingModeName.AXIAL:
                default:
                    mapper.setSlicingMode(SlicingMode.Z);
                    break;
            }

            actor.setMapper(mapper);

            // --- set up default window/level ---
            actor.getProperty().setColorWindow(1200);
            actor.getProperty().setColorLevel(80);
            actor.getProperty().setInterpolationType(InterpolationType.NEAREST);

            let onModifiedSub = null;
            let imageCenter;

            // --- Segmentation ---
            const widgetManager = vtkWidgetManager.newInstance();
            widgetManager.setRenderer(renderer);

            const paintWidget = vtkPaintWidget.newInstance();
            const paintHandle = widgetManager.addWidget(
                paintWidget,
                ViewTypes.SLICE,
            );
            const painter = vtkPaintFilter.newInstance();

            if (eventNames?.CaptureCustomScreenshot) {
                window.addEventListener(
                    eventNames.CaptureCustomScreenshot,
                    captureCustomScreenshot,
                );
            }

            paintHandle.onStartInteractionEvent(() => {
                const point = paintWidget.getWidgetState().getTrueOrigin();
                const controlKey = paintWidget.getWidgetState().getControlKey();
                const sliceLocation = mapper.getSlice();
                const imageData = mapper.getInputData();
                const imageBounds = imageData ? imageData.getBounds() : null;

                // BRUSH FIX: Ensure the manipulator origin is properly set for brush outline visibility
                if (imageBounds && imageData) {
                    const slicingMode = mapper.getSlicingMode() % 3;
                    const minBound = imageBounds[slicingMode * 2];
                    const maxBound = imageBounds[slicingMode * 2 + 1];
                    const imageSpacing = imageData.getSpacing();
                    const spacing = imageSpacing[slicingMode];

                    const adjustment = getManipulatorAdjustment(
                        sliceLocation,
                        minBound,
                        maxBound,
                        spacing,
                    );

                    if (adjustment !== null) {
                        const center = imageData.getCenter();
                        const adjustedOrigin = [
                            center[0],
                            center[1],
                            center[2],
                        ];
                        adjustedOrigin[slicingMode] = adjustment;
                        paintWidget
                            .getManipulator()
                            .setUserOrigin(adjustedOrigin);
                        paintHandle.updateRepresentationForRender();
                        renderWindow.render();
                    }
                }

                // BRUSH FIX: Correct the Z coordinate for painting on boundary slices
                const correctedPoint = [...point];
                if (imageBounds && imageData) {
                    const slicingMode = mapper.getSlicingMode() % 3;
                    const minBound = imageBounds[slicingMode * 2];
                    const maxBound = imageBounds[slicingMode * 2 + 1];
                    correctedPoint[slicingMode] = getCorrectedPaintZ(
                        sliceLocation,
                        minBound,
                        maxBound,
                    );
                }
                painter.setLabelForClickedPoint(correctedPoint, controlKey);
                painter.startStroke();
                painter.addPoint(correctedPoint);
            });

            paintHandle.onEndInteractionEvent(() => {
                painter?.endStroke()?.then(() => {
                    // TODO: This may need to be tweaked for MPR in the future, as you could be drawing on multiple slices
                    const worldCoords = paintWidget
                        .getWidgetState()
                        .getTrueOrigin();

                    // BRUSH FIX: Use corrected coordinates for slice index calculation on boundary slices
                    const imageData = mapper.getInputData();
                    const imageBounds = imageData
                        ? imageData.getBounds()
                        : null;
                    const sliceLocation = mapper.getSlice();

                    const correctedWorldCoords = [...worldCoords];
                    if (imageBounds && imageData) {
                        const slicingMode = mapper.getSlicingMode() % 3;
                        const minBound = imageBounds[slicingMode * 2];
                        const maxBound = imageBounds[slicingMode * 2 + 1];
                        correctedWorldCoords[slicingMode] = getCorrectedPaintZ(
                            sliceLocation,
                            minBound,
                            maxBound,
                        );
                    }

                    // Zero-based slice index.
                    // WARNING: If the user moves the slice plane with CTRL+Click+Drag, this will have a fractional offset.
                    // Use Math.round since getSliceAtPosition returns a fractional world-to-index
                    // value (e.g. 238.9 for slice 239). Math.floor would give the wrong slice.
                    const sliceIdxFromMapper =
                        maskMapper.getSliceAtPosition(correctedWorldCoords);
                    const sliceIdxRounded = Math.round(sliceIdxFromMapper);

                    log(`Adding contour data for slice ${sliceIdxFromMapper}`);
                    // Extract pixel data for this slice.
                    // 1) Pull out the full vtkImageData for the label-map
                    const maskImage = painter.getOutputData();

                    // 2) Query its dimensions (number of voxels in X, Y, Z)
                    const [nx, ny, nz] = maskImage.getDimensions();

                    // 3) Grab the raw TypedArray of label values
                    const scalars = maskImage
                        .getPointData()
                        .getScalars()
                        .getData(); // e.g. Int16Array of length nx*ny*nz

                    // 4) Compute slice size & offset
                    const sliceSize = nx * ny;
                    const offset = sliceIdxRounded * sliceSize;

                    // 5) Grab just that slice’s data
                    const sliceBuffer = scalars.subarray(
                        offset,
                        offset + sliceSize,
                    );

                    // Now `sliceBuffer` is a 1D int16Array of length nx*ny,
                    // laid out row-major: [ (x=0,y=0), (x=1,y=0), …, (x=nx-1,y=0),
                    //                       (x=0,y=1), …, (x=nx-1,y=1),
                    //                       …,
                    //                       (x=nx-1,y=ny-1) ]

                    const dimensions = maskImage.getDimensions();
                    const sliceDimensions = [dimensions[0], dimensions[1], 1];

                    const data = {
                        buffer: sliceBuffer,
                        dimensions: sliceDimensions,
                        origin: maskImage.getOrigin(),
                        spacing: maskImage.getSpacing(),
                    };
                    if (isCenterline) {
                        // Invoke the callback.
                        centerlineSegmentationChangedCallbackRef.current(
                            sliceIdxRounded,
                            data,
                        );
                    } else {
                        axialSegmentationChangedCallbackRef.current(
                            sliceIdxRounded,
                            data,
                        );
                    }
                });
            });

            paintHandle.onInteractionEvent(() => {
                const point = paintWidget.getWidgetState().getTrueOrigin();

                // BRUSH FIX: Correct the Z coordinate for painting on boundary slices
                const imageData = mapper.getInputData();
                const imageBounds = imageData ? imageData.getBounds() : null;
                const sliceLocation = mapper.getSlice();

                const correctedPoint = [...point];
                if (imageBounds && imageData) {
                    const slicingMode = mapper.getSlicingMode() % 3;
                    const minBound = imageBounds[slicingMode * 2];
                    const maxBound = imageBounds[slicingMode * 2 + 1];
                    correctedPoint[slicingMode] = getCorrectedPaintZ(
                        sliceLocation,
                        minBound,
                        maxBound,
                    );
                }

                painter.addPoint(correctedPoint);
            });

            if (
                getDUName(activeWidget) === WidgetType.SEGMENTATION_WIDGET &&
                segmentationProps.paintingEnabled
            ) {
                widgetManager.grabFocus(paintWidget);
            }

            paintHandle.setVisibility(segmentationProps.paintingEnabled);
            paintHandle.updateRepresentationForRender();

            const brushRadius = brushSizeToRadius(
                segmentationProps.paintingBrushSize,
            );
            paintWidget.setRadius(brushRadius);
            painter.setRadius(brushRadius);

            const maskActor = vtkImageSlice.newInstance();
            const maskMapper = vtkImageMapper.newInstance();

            maskMapper.setSliceAtFocalPoint(true);
            maskMapper.setSlicingMode(SlicingMode.Z);

            maskActor.setMapper(maskMapper);

            context.current.segmentationLabel = 1;
            maskMapper.setInputConnection(painter.getOutputPort());

            // Store all VTK objects on context so other useEffects and cleanup can access them.
            context.current = {
                ...context.current,
                genericRenderWindow,
                renderer,
                renderWindow,
                istyle,
                interactor,
                actor,
                mapper,
                maskActor,
                maskMapper,
                widgetManager,
                paintWidget,
                paintHandle,
                painter,
                combinedTouchStart,
                combinedTouchEnd,
                handleWheel,
            };

            const opFun = vtkPiecewiseFunction.newInstance();
            opFun.addPoint(0, 0); // our background value, 0, will be invisible
            opFun.addPoint(0.5, 1);
            opFun.addPoint(1, 1);

            const cFun = getTransferFunctionByColor(segmentationColor);

            const prop = maskActor.getProperty();

            prop.setRGBTransferFunction(0, cFun);
            prop.setScalarOpacity(0, opFun);

            prop.setInterpolationTypeToNearest();
            prop.setUseLookupTableScalarRange(true);
            prop.setUseLabelOutline(true);
            prop.setLabelOutlineThickness(
                segmentationProps.segmentationBorderThickness,
            );
            prop.setLabelOutlineOpacity(
                segmentationProps.segmentationBorderOpacity,
            );
            prop.setOpacity(segmentationProps.segmentationOpacity);

            imageLoader.tryGetMask(maskFiles).then((imageData) => {
                if (isNullOrUndefined(imageData)) return;
                painter.setLabelMap(imageData);
                maskMapper.setInputConnection(painter.getOutputPort());
                _updateMaskSlices(maskFiles, modifiedSegmentations);
                renderWindow.render();

                const backgroundValue = isCenterline
                    ? CENTERLINE_EMPTY_SEGMENTATION_LABEL
                    : AXIAL_EMPTY_SEGMENTATION_LABEL;

                const foundLabel = imageData
                    .getPointData()
                    .getScalars()
                    .getData()
                    .find((value) => {
                        // Find the first value that isn't a background number
                        // This might lag a touch
                        return value !== backgroundValue;
                    });

                if (typeof foundLabel !== "undefined") {
                    context.current.segmentationLabel = foundLabel;

                    const opFunNew = vtkPiecewiseFunction.newInstance();
                    opFunNew.addPoint(backgroundValue, 0); // our background value, 0, will be invisible
                    opFunNew.addPoint(context.current.segmentationLabel, 1);
                    maskActor.getProperty().setPiecewiseFunction(opFunNew);
                } else {
                    console.error(
                        "Unable to determine label mask, falling back to blank segmentation",
                    );
                }
            });

            // TODO: VRM-4359 Can we share the vtkImageData across DICOM viewports? This could be useful for
            //  [Axial / Sagittal / Coronal] and [Centerline Plane A / Plane B].
            imageLoader.tryGetImage(imageFiles).then((imageData) => {
                if (isNullOrUndefined(imageData)) return;

                if (!mapper.isDeleted()) {
                    debug(() => (global.debug_imageData = imageData));
                    const baseBounds = mapper.getBounds();
                    const bounds = Vector3.ofBounds(baseBounds);
                    const minIndex = vec3.create();
                    const maxIndex = vec3.create();
                    imageData.worldToIndex(bounds.min.toVtk(), minIndex);
                    imageData.worldToIndex(bounds.max.toVtk(), maxIndex);

                    let maxSliceIndex;
                    switch (slicingMode.name) {
                        case SlicingModeName.SAGITTAL:
                            maxSliceIndex = maxIndex[0];
                            break;
                        case SlicingModeName.CORONAL:
                            maxSliceIndex = maxIndex[1];
                            break;
                        case SlicingModeName.AXIAL:
                        default:
                            maxSliceIndex = maxIndex[2];
                            break;
                    }

                    imageCenter = imageData.getCenter();

                    context.current = {
                        ...context.current,
                        imageCenter,
                    };

                    mapper.setInputData(imageData);
                    painter.setBackgroundImage(imageData);

                    if (onBoundsAcquired) {
                        const imgBounds = imageData.getBounds();
                        const slicingModeAxis = context.current.mapper.getSlicingMode() % 3;
                        const minBound = imgBounds[slicingModeAxis * 2];
                        const maxBound = imgBounds[slicingModeAxis * 2 + 1];

                        onBoundsAcquired({
                            viewportId: viewportId,
                            bounds: {
                                firstIndex: minBound,
                                lastIndex: maxBound,
                            }
                        });
                    }

                    const slicingModeAxis = mapper.getSlicingMode() % 3;

                    let initialSlice;
                    if (isCenterline) {
                        // Centerline slices start from 0, but vtkImageMapper thinks 0 is out of bounds, so we add a small offset.
                        initialSlice = 0.1;
                    } else {
                        // Get the slicing mode to determine which axis we're slicing along
                        initialSlice = imageCenter[slicingModeAxis];
                    }

                    log("Setting up slice change callback");
                    onModifiedSub = mapper.onModified(
                        /**
                         * @param {import('vtk.js/Sources/Rendering/Core/ImageMapper').default} modifiedMapper
                         */
                        function (modifiedMapper) {
                            let sliceLocation = modifiedMapper.getSlice();
                            let bounds = modifiedMapper.getBoundsForSlice();
                            maskMapper.setSlice(sliceLocation);
                            log(
                                "Calling onSliceChange with",
                                sliceLocation,
                                maxSliceIndex,
                                bounds,
                            );

                            const slicingMode = mapper.getSlicingMode() % 3;

                            if (slicingMode > -1) {
                                // The sliceLocation from getSlice() is in world coordinates when setSliceAtFocalPoint(true).
                                // We need to set the manipulator origin using world coordinates on the current slice plane.
                                const center = imageData.getCenter();
                                const imgBounds = imageData.getBounds();
                                const worldOrigin = [
                                    center[0],
                                    center[1],
                                    center[2],
                                ];

                                const minBound = imgBounds[slicingMode * 2];
                                const maxBound = imgBounds[slicingMode * 2 + 1];

                                // BRUSH FIX: For boundary slices, use a position inside the bounds for the manipulator
                                // This ensures the brush outline is visible when possible
                                const imageSpacing = imageData.getSpacing();
                                const spacing = imageSpacing[slicingMode];

                                let manipulatorZ = sliceLocation;

                                // Check whether the manipulator needs to be nudged inside the bounds
                                const adjustedZ = getManipulatorAdjustment(
                                    sliceLocation,
                                    minBound,
                                    maxBound,
                                    spacing,
                                );
                                if (adjustedZ !== null) {
                                    manipulatorZ = adjustedZ;
                                }

                                worldOrigin[slicingMode] = manipulatorZ;

                                paintWidget
                                    .getManipulator()
                                    .setUserOrigin(worldOrigin);

                                // BRUSH FIX: Also set the manipulator's normal to ensure proper orientation
                                const normal = [0, 0, 0];
                                normal[slicingMode] = 1;
                                paintWidget
                                    .getManipulator()
                                    .setUserNormal(normal);

                                painter.setSlicingMode(slicingMode);
                                paintHandle.updateRepresentationForRender();
                                maskMapper.set(
                                    mapper.get("slice", "slicingMode"),
                                );

                                renderWindow.render();
                            }

                            if (onSliceChange) {
                                onSliceChange({
                                    index: sliceLocation,
                                    maxIndex: maxSliceIndex,
                                    bounds,
                                });
                            }
                        },
                    );

                    // --- Add image slice actor to scene ---
                    renderer.addActor(actor);
                    if (showMasks) {
                        renderer.addActor(maskActor);
                    }

                    // DEBUG - Add cone for reference
                    // const coneSource = vtkConeSource.newInstance({ height: 100.0, center: [ 5.801562499999989, -0.3984375, -124.25 ], radius: 100, direction: [0,0,1] });
                    // const coneMapper = vtkMapper.newInstance();
                    // coneMapper.setInputConnection(coneSource.getOutputPort());
                    // const coneActor = vtkActor.newInstance();
                    // coneActor.setMapper(coneMapper);
                    // renderer.addActor(coneActor);
                    // global.coneSource = coneSource;
                    // global.coneActor = coneActor;

                    // --- Reset camera and render the scene ---

                    let camera = renderer.getActiveCamera();
                    debug(() => (global.debug_camera = camera));

                    switch (slicingMode.name) {
                        case SlicingModeName.SAGITTAL:
                            camera.setDirectionOfProjection(1, 0, 0);
                            renderer.resetCamera();
                            camera.setViewUp(Vector3.unitZ());
                            break;
                        case SlicingModeName.CORONAL:
                            camera.setDirectionOfProjection(0, 1, 0);
                            renderer.resetCamera();
                            camera.setViewUp(Vector3.unitZ());
                            break;
                        case SlicingModeName.AXIAL:
                        default:
                            // Set the camera to be oriented feet-to-head.
                            camera.setDirectionOfProjection(0, 0, 1);
                            renderer.resetCamera();
                            camera.setViewUp(
                                vtkMath.multiplyScalar(Vector3.unitY(), -1),
                            );
                            break;
                    }

                    // Zoom in to fit the image in the viewport.
                    if (isCenterline) {
                        // Try to calculate parallel scale now, but it will be recalculated when the container becomes visible.
                        calculateCenterlineParallelScale();
                        // If container is not visible yet (width/height = 0), use a fallback scale.
                        if (
                            !containerRef.current?.clientWidth ||
                            !containerRef.current?.clientHeight
                        ) {
                            camera.setParallelScale(
                                camera.getParallelScale() / 5,
                            );
                        }
                    } else {
                        camera.setParallelScale(
                            camera.getParallelScale() / 2.5,
                        );
                    }

                    // Reset the camera clipping range to fit the image, otherwise we won't see anything.
                    renderer.resetCameraClippingRange();

                    renderer.setActiveCamera(camera);

                    if (isCenterline) {
                        // For some reason, mapper.setSlice doesn't behave as expected for the centerline DICOMs, so
                        // we have to set the focal point instead.
                        const focalPoint = camera.getFocalPoint();
                        camera.setFocalPoint(
                            focalPoint[0],
                            focalPoint[1],
                            initialSlice,
                        );
                    } else {
                        mapper.setSlice(initialSlice);
                        maskMapper.setSlice(initialSlice);
                    }

                    log("Rendering DICOM...");
                    renderWindow.render();
                    log("Finished rendering DICOM");

                    // Store the onModified subscription so cleanup can unsubscribe.
                    context.current.onModifiedSub = onModifiedSub;

                    onVisualizationReady?.([viewportId, true]);
                } else {
                    log("Aborted rendering DICOM, image mapper was disposed");
                }
            });
        }

        // --- Expose globals so we can play with values in the dev console ---

        //global.dicomGenericRenderWindow = genericRenderWindow;
        //global.dicomRenderWindow = renderWindow;
        //global.dicomRenderer = renderer;
        //global.dicomActor = actor;
        //global.dicomMapper = mapper;
    }, [imageFiles, viewportId]);

    // Effect to update the resize observer.
    useEffect(() => {
        if (context.current) {
            if (isVisible) {
                if (context.current.resizeObserver) {
                    context.current.resizeObserver.disconnect();
                }

                const resizeObserver = new ResizeObserver(handleResize);
                resizeObserver.observe(containerRef.current);
                context.current.resizeObserver = resizeObserver;
            } else {
                if (context.current.resizeObserver) {
                    context.current.resizeObserver.disconnect();
                    context.current.resizeObserver = null;
                }
            }
        }
    }, [isVisible]);

    // Screenshot side effect
    const [captureScreenshot, ...screenshotInteraction] = useScreenshot(
        containerRef.current,
        context.current,
        null,
        screenshotProps,
    );

    // Side effect to set the mouse wheel scroll mode.
    useEffect(() => {
        if (context.current?.istyle) {
            context.current.istyle.setMouseWheelIsSliceScroll(isSliceScroll);
        }
    }, [isSliceScroll]);

    //Side effect to show or hide the segmentation masks
    useEffect(() => {
        if (
            context.current.renderer &&
            context.current.renderWindow &&
            context.current.maskActor
        ) {
            showMasks
                ? context.current.renderer.addActor(context.current.maskActor)
                : context.current.renderer.removeActor(
                    context.current.maskActor,
                );
            context.current.renderWindow.render();
        }
    }, [showMasks]);

    // Sync mask opacity and paint/erase widget state whenever segmentationProps change
    useEffect(() => {
        if (context.current && context.current.maskActor) {
            const cFun = getTransferFunctionByColor(segmentationColor);
            context.current.maskActor
                .getProperty()
                .setRGBTransferFunction(cFun);
            context.current.maskActor
                .getProperty()
                .setOpacity(segmentationProps.segmentationOpacity);
            context.current.maskActor
                .getProperty()
                .setLabelOutlineOpacity(
                    segmentationProps.segmentationBorderOpacity,
                );
            //Prevents a fully opaque border from hiding segmentation
            if (segmentationProps.segmentationBorderOpacity === 0) {
                context.current.maskActor
                    .getProperty()
                    .setLabelOutlineThickness(0);
            } else {
                context.current.maskActor
                    .getProperty()
                    .setLabelOutlineThickness(
                        segmentationProps.segmentationBorderThickness,
                    );
            }
            //Update the brush mode
            context.current.painter.setPaintingMode(getDUName(segmentationProps.paintingMode));

            if (
                getDUName(activeWidget) === WidgetType.SEGMENTATION_WIDGET &&
                segmentationProps.paintingEnabled
            ) {
                context.current.widgetManager.grabFocus(
                    context.current.paintWidget,
                );
            } else {
                context.current.widgetManager.releaseFocus();
            }
            context.current.paintHandle.setVisibility(
                segmentationProps.paintingEnabled,
            );
            context.current.paintHandle.updateRepresentationForRender();

            const brushRadius = brushSizeToRadius(
                segmentationProps.paintingBrushSize,
            );
            context.current.paintWidget.setRadius(brushRadius);
            context.current.painter.setRadius(brushRadius);

            context.current.renderWindow.render();
        }
    }, [segmentationProps, activeWidget]);

    useEffect(() => {
        modifiedSegmentationsRef.current = modifiedSegmentations;
        if (context.current?.painter) {
            _updateMaskSlices(maskFilesRef.current, modifiedSegmentations);
            context.current.renderWindow?.render();
        }
    }, [modifiedSegmentations]);

    useEffect(() => {
        if (!context.current) return;

        maskFilesRef.current = maskFiles;

        imageLoader.tryGetMask(maskFiles).then((vtkImage) => {
            if (isNullOrUndefined(vtkImage)) return;

            const ctx = context.current;
            if (!ctx?.painter || !ctx?.maskMapper || !ctx?.renderWindow) return;
            ctx.painter.setLabelMap(vtkImage);
            ctx.maskMapper.setInputConnection(ctx.painter.getOutputPort());
            _updateMaskSlices(maskFilesRef.current, modifiedSegmentationsRef.current);
            ctx.renderWindow.render();
        });

    }, [maskFiles]);

    // Side effect for repositioning the camera when the slice plane index changes.
    useEffect(() => {
        // This use effect is for syncing external slice changes
        // unfortunately, changing the slice from within the component will trigger this as well
        // Thus, we ignore these changes
        if (ignoreSliceChangeRef.current) {
            ignoreSliceChangeRef.current = false;
            return;
        }

        if (
            slicingMode.name !== SlicingModeName.AXIAL ||
            !context.current?.renderer ||
            !slicePlaneIndex
        ) {
            return;
        }

        let slice = context.current.mapper.getSlice();
        let diff = slice - slicePlaneIndex;

        if (diff === 0) {
            return;
        }
        log(`Current slice: ${slice}, requested slice: ${slicePlaneIndex}`);

        // Clamp the requested slice to the image bounds to prevent scrolling past the volume.
        let clampedSlice = slicePlaneIndex;
        const imageData = context.current.mapper.getInputData();
        if (imageData) {
            const imgBounds = imageData.getBounds();
            const slicingModeAxis = context.current.mapper.getSlicingMode() % 3;
            const minBound = imgBounds[slicingModeAxis * 2];
            const maxBound = imgBounds[slicingModeAxis * 2 + 1];
            clampedSlice = Math.max(minBound, Math.min(maxBound, clampedSlice));
        }

        const camera = context.current.renderer.getActiveCamera();
        const focalPoint = [...camera.getFocalPoint()];
        const position = [...camera.getPosition()];
        const delta = clampedSlice - focalPoint[2];
        focalPoint[2] = clampedSlice;
        position[2] += delta;

        log(`Setting focal point to ${focalPoint}`);
        camera.setFocalPoint(...focalPoint);
        camera.setPosition(...position);
        context.current.renderWindow.render();
    }, [slicePlaneIndex, slicingMode]);

    // Interaction side effect
    useInteraction(
        null,
        interactionModeProps,
        null,
        null,
        screenshotInteraction,
        captureScreenshot,
        null,
        null,
        null,
        null,
    );

    // Length tool side effect
    if (lengthProps)
        useDistanceWidget(
            context.current,
            lengthProps,
            slicePlaneIndex,
            activeWidget,
            slicingMode,
        );

    // Angle tool side effect
    if (angleProps)
        useAngleWidget(
            context.current,
            angleProps,
            slicePlaneIndex,
            activeWidget,
            slicingMode,
        );

    if (!isCenterline) {
        useCrosshairsWidget(
            context.current,
            crosshairsWidgetFactory,
            activeWidget,
            slicePlaneIndex,
        );
    } else {
        useDiameterLines(
            context.current,
            slicePlaneIndex,
            centerlineSegmentationChangedCallbackRef.current,
            maskFilesRef.current,
            !!activeMeasurement,
            diameterLinesTrigger
        );
    }

    // Side effect to force rendering when the component becomes visible.
    useEffect(() => {
        if (
            context.current?.renderWindow &&
            context.current?.genericRenderWindow &&
            isVisible
        ) {
            render();
            // Recalculate parallel scale for centerline when becoming visible.
            if (isCenterline) {
                // If this is the first time becoming visible, allow auto-scaling.
                // After that, preserve user's zoom preference.
                if (!hasBeenVisibleRef.current) {
                    hasManualZoomRef.current = false;
                    manualZoomStateByViewport.set(viewportId, false);
                    hasBeenVisibleRef.current = true;
                }

                // Only recalculate if user hasn't manually zoomed.
                if (!hasManualZoomRef.current) {
                    calculateCenterlineParallelScale();
                }

                context.current.renderWindow.render();
            }
        }
    }, [isVisible]);

    // Side effect to set the slice scroll callback.
    useEffect(() => {
        if (context.current?.istyle) {
            if (isCenterline) {
                context.current.istyle.setSliceScrollCallback(
                    onCenterlineSliceScroll,
                );
            } else {
                context.current.istyle.setSliceScrollCallback(null);
            }
        }
    }, [isCenterline]);

    // Side effect to update the centerline slice scroll callback.
    useEffect(() => {
        if (isCenterline && context.current?.istyle) {
            context.current.istyle.setSliceScrollCallback(
                onCenterlineSliceScroll,
            );
        }
    }, [isCenterline, onCenterlineSliceScroll]);

    // Side effect to handle viewport screenshot capture events.
    useEffect(() => {
        if (
            !eventNames ||
            !eventNames.CaptureDicomVisualizationScreenshot ||
            !viewportId
        ) {
            return;
        }

        const eventName = `${eventNames.CaptureDicomVisualizationScreenshot}_${viewportId}`;

        const handleScreenshotCapture = (ev) => {
            const { genericRenderWindow, konvaStage } = context.current;
            if (!(ev.detail && genericRenderWindow && konvaStage)) {
                return;
            }

            const { callback } = ev.detail;
            if (typeof callback !== "function") {
                console.error(
                    "Invalid callback for viewport screenshot capture",
                );
                return;
            }

            try {
                const dpr = window.devicePixelRatio || 1;
                const renderWindow = genericRenderWindow.getRenderWindow();

                const waitForCanvasReady = () => {
                    const vtkCanvas = genericRenderWindow
                        .getApiSpecificRenderWindow()
                        .getCanvas();
                    if (!vtkCanvas) {
                        console.error(
                            "VTK canvas not available for screenshot",
                        );
                        callback("");
                        return;
                    }

                    const konvaCanvas = konvaStage.toCanvas();
                    if (!konvaCanvas) {
                        console.error(
                            "Konva canvas not available for screenshot",
                        );
                        callback("");
                        return;
                    }

                    const canvasReady =
                        vtkCanvas.width > 0 &&
                        vtkCanvas.height > 0 &&
                        konvaCanvas.width > 0 &&
                        konvaCanvas.height > 0;

                    if (!canvasReady) {
                        renderWindow.render();
                        konvaStage.draw();
                        requestAnimationFrame(waitForCanvasReady);
                        return;
                    }

                    // Double RAF to ensure the browser has fully applied layout + paint before capturing canvas
                    requestAnimationFrame(() => {
                        renderWindow.render();
                        konvaStage.draw();

                        requestAnimationFrame(() => {
                            renderWindow.render();
                            konvaStage.draw();

                            const latestVtkCanvas = genericRenderWindow
                                .getApiSpecificRenderWindow()
                                .getCanvas();
                            const latestKonvaCanvas = konvaStage.toCanvas();

                            capture(latestVtkCanvas, latestKonvaCanvas);
                        });
                    });
                };

                const capture = (vtkCanvas, konvaCanvas) => {
                    const compositeCanvas = document.createElement("canvas");
                    const ctx = compositeCanvas.getContext("2d");
                    if (!ctx) {
                        console.error(
                            "2D context not available for screenshot",
                        );
                        callback("");
                        return;
                    }

                    compositeCanvas.width = vtkCanvas.width / dpr;
                    compositeCanvas.height = vtkCanvas.height / dpr;

                    ctx.drawImage(
                        vtkCanvas,
                        0,
                        0,
                        compositeCanvas.width,
                        compositeCanvas.height,
                    );

                    ctx.drawImage(
                        konvaCanvas,
                        0,
                        0,
                        compositeCanvas.width,
                        compositeCanvas.height,
                    );

                    const dataUrl = compositeCanvas.toDataURL("image/png");
                    callback(dataUrl.split(",")[1] || "");
                };

                requestAnimationFrame(waitForCanvasReady);
            } catch (error) {
                console.error("Error capturing viewport screenshot:", error);
                callback("");
            }
        };

        window.addEventListener(eventName, handleScreenshotCapture);

        return () => {
            window.removeEventListener(eventName, handleScreenshotCapture);
        };
    }, [eventNames, viewportId]);

    return <div className="layout-view-view-container" ref={containerRef} />;
};

export default DicomVisualization;